import type { RuntimeBridge } from "../types/RuntimeBridge"
import type { WasmImports } from "../maps/Signatures"
import type {
  WasmAllocated,
  WasmInstance,
  WasmPointer,
} from "../types/WasmInstance"
import type { WasmBinary } from "../utils/ResolveWasm"
import { UrlLoad } from "@pdfking/compatibility"
import { Ceil, Max, Min } from "@pdfking/math"
import {
  Constant,
  IsFunction,
  Items,
  Keys,
  RecordAssign,
  RecordMap,
  UInt8ToString,
  Void,
} from "@pdfking/tfunc"
import { BuildFSImports } from "@pdfking/wasm-core/compatibility"
import { DefaultMinimap as M } from "../maps/DefaultMinimap"
import { InvokeMap } from "../maps/InvokeMap"
import { ResolveBinary } from "../utils/ResolveWasm"

// ── ConvertJsFunctionToWasm helpers ─────────────────────────────────

const WASM_TYPE_NAMES: Readonly<Record<string, string>> = {
  d: "f64",
  f: "f32",
  i: "i32",
  j: "i64",
  p: "i32",
}

const WASM_TYPE_CODES: Readonly<Record<string, number>> = {
  d: 124,
  f: 125,
  i: 127,
  j: 126,
  p: 127,
}

const SigToWasmTypes = (sig: string) => ({
  parameters: [...sig.slice(1)].map(c => WASM_TYPE_NAMES[c]),
  results: sig[0] === "v" ? [] : [WASM_TYPE_NAMES[sig[0]]],
})

const Uleb128Encode = (n: number, target: number[]) => {
  if (n < 128) target.push(n)
  else target.push((n % 128) | 128, n >> 7)
}

const GenerateFuncType = (sig: string, target: number[]) => {
  const sigParam = sig.slice(1)
  target.push(96)
  Uleb128Encode(sigParam.length, target)
  for (const c of sigParam) target.push(WASM_TYPE_CODES[c])
  if (sig[0] === "v") target.push(0)
  else target.push(1, WASM_TYPE_CODES[sig[0]])
}

const ConvertJsFunctionToWasm = (
  func: (...args: number[]) => number | bigint,
  sig: string
): WebAssembly.ExportValue => {
  const WasmFunction = (WebAssembly as unknown as Record<string, unknown>)
    .Function as new (
    type: unknown,
    func: unknown
  ) => WebAssembly.ExportValue
  if (IsFunction(WasmFunction)) {
    return new WasmFunction(SigToWasmTypes(sig), func)
  }
  const typeSectionBody: number[] = [1]
  GenerateFuncType(sig, typeSectionBody)
  const bytes: number[] = [0, 97, 115, 109, 1, 0, 0, 0, 1]
  Uleb128Encode(typeSectionBody.length, bytes)
  bytes.push(...typeSectionBody)
  bytes.push(2, 7, 1, 1, 101, 1, 102, 0, 0, 7, 5, 1, 1, 102, 0, 0)
  const module = new WebAssembly.Module(new Uint8Array(bytes))
  const instance = new WebAssembly.Instance(module, { e: { f: func } })
  return instance.exports.f
}

// ── Constants ───────────────────────────────────────────────────────

const Fail = (msg: string): never => {
  throw new Error(msg)
}

const noop = Void
const zero = Constant(0)
const enoent = Constant(-44)
const ebadf = Constant(8)
const ebadf_neg = Constant(-8)
const abort = () => Fail("abort")
const throw_longjmp = (): never => {
  throw Infinity
}
const assert_fail = () => Fail("assertion failed")
const cxa_throw = (ptr: number | bigint) => {
  throw ptr
}
const exit_fn = (status: number): never => {
  throw { status }
}
const date_now = Constant(1704067200000)

const MAX_HEAP_32 = 4294967296 // 4 GB
const MAX_HEAP_64 = 17179869184 // 16 GB
const Align = (size: number, alignment: number) =>
  Ceil(size / alignment) * alignment

// ── BuildImports ────────────────────────────────────────────────────

type T = number | bigint

const BuildImports = (
  memory64: boolean,
  exports: Record<string, unknown>,
  conv: (v: number | bigint) => T
): WasmImports<T> => {
  const mem = () => exports[M.memory as string] as WebAssembly.Memory
  const maxHeap = memory64 ? MAX_HEAP_64 : MAX_HEAP_32

  const base = {
    [M._tzset_js]: noop,
    [M._localtime_js]: noop,
    [M._gmtime_js]: noop,
    [M._emscripten_runtime_keepalive_clear]: noop,
    [M._setitimer_js]: zero,
    [M.__syscall_rmdir]: enoent,
    [M.__syscall_getdents64]: ebadf_neg,
    [M.__syscall_dup]: ebadf_neg,
    [M.__syscall_dup3]: ebadf_neg,
    [M.__syscall_lstat64]: enoent,
    [M.__syscall_newfstatat]: enoent,
    [M.environ_get]: zero,
    [M.fd_pread]: ebadf,
    [M.fd_pwrite]: ebadf,
    [M.clock_time_get]: zero,
    [M.__syscall_fcntl64]: zero,
    [M.__syscall_ioctl]: zero,
    [M.__syscall_openat]: enoent,
    [M.__syscall_stat64]: enoent,
    [M.__syscall_unlinkat]: enoent,
    [M.__syscall_fstat64]: enoent,
    [M.__syscall_renameat]: enoent,
    [M.fd_close]: ebadf,
    [M.fd_write]: ebadf,
    [M.fd_read]: ebadf,
    [M.fd_seek]: ebadf,
    [M.emscripten_date_now]: date_now,
    [M._abort_js]: abort,
    [M._emscripten_throw_longjmp]: throw_longjmp,
    [M.__assert_fail]: assert_fail,
    [M.__cxa_throw]: cxa_throw,
    [M.exit]: exit_fn,
    [M.proc_exit]: exit_fn,
    [M.log_str]: (ptr: number | bigint, len: number) => {
      console.log(
        UInt8ToString(new Uint8Array(mem().buffer, Number(ptr), len))
      )
    },
    [M.environ_sizes_get]: (
      countPtr: number | bigint,
      sizePtr: number | bigint
    ) => {
      const heap = new DataView(mem().buffer)
      const cp = Number(countPtr)
      const sp = Number(sizePtr)
      if (memory64) {
        heap.setBigUint64(cp, 0n, true)
        heap.setBigUint64(sp, 0n, true)
      } else {
        heap.setInt32(cp, 0, true)
        heap.setInt32(sp, 0, true)
      }
      return 0
    },
    [M.__syscall_getcwd]: (buf: number | bigint) => {
      const b = Number(buf)
      const heap = new DataView(mem().buffer)
      heap.setUint8(b, 47)
      heap.setUint8(b + 1, 0)
      return b
    },
    [M.emscripten_resize_heap]: (requestedSize: number | bigint) => {
      const m = mem()
      const oldSize = m.buffer.byteLength
      const rs = Number(requestedSize)
      const requested = memory64 ? rs : rs >>> 0
      if (requested > maxHeap) return 0
      for (let cutDown = 1; cutDown <= 4; cutDown *= 2) {
        let overGrownHeapSize = oldSize * (1 + 0.2 / cutDown)
        overGrownHeapSize = Min(overGrownHeapSize, requested + 100663296)
        const newSize = Min(
          maxHeap,
          Align(Max(requested, overGrownHeapSize), 65536)
        )
        const pagesToGrow = ((newSize - oldSize + 65535) / 65536) | 0
        try {
          m.grow((memory64 ? BigInt(pagesToGrow) : pagesToGrow) as number)
          return 1
        } catch {
          // retry with less overallocation
        }
      }
      return 0
    },
  } satisfies Partial<WasmImports<T>>

  const fs = BuildFSImports({
    exports: exports as WasmInstance["exports"],
    conv,
    memory64,
  })

  const invokes = RecordMap(
    (dynCallName: string) =>
      (
        index: number | bigint,
        ...args: (number | bigint)[]
      ): number | bigint | undefined => {
        const stackGet = exports[M.emscripten_stack_get_current as string] as
          | (() => number | bigint)
          | undefined
        const sp = stackGet?.()
        try {
          const dynCall = exports[dynCallName] as
            | ((...a: (number | bigint)[]) => number | bigint)
            | undefined
          if (dynCall) return dynCall(index, ...args)
          const table = exports[
            M.__indirect_function_table as string
          ] as WebAssembly.Table
          const fn = table.get(
            (memory64 ? index : Number(index)) as number
          ) as
            | ((...a: (number | bigint)[]) => number | bigint)
            | null
          if (fn) return fn(...args)
        } catch (e: unknown) {
          const stackRestore = exports[
            M._emscripten_stack_restore as string
          ] as (sp: number | bigint) => void
          stackRestore(sp!)
          if (typeof e !== "number" && typeof e !== "bigint") throw e
          const setThrew = exports[M.setThrew as string] as (
            a: number | bigint,
            b: number
          ) => void
          setThrew(memory64 ? BigInt(1) : 1, 0)
        }
        return undefined
      },
    InvokeMap
  )

  return { ...base, ...fs, ...invokes } as WasmImports<T>
}

// ── BuildAllImports ─────────────────────────────────────────────────

const BuildAllImports = (memory64: boolean) => {
  const exports = {} as Record<string, unknown>
  const conv: (v: number | bigint) => T = memory64
    ? v => BigInt(v)
    : v => Number(v)
  const all = BuildImports(memory64, exports, conv)

  const moduleImports: WebAssembly.ModuleImports = {}
  for (const [name, fn] of Items(all)) {
    moduleImports[name] = fn as WebAssembly.ModuleImports[string]
  }

  return {
    exports,
    conv,
    imports: {
      [M.env as string]: moduleImports,
      [M.wasi_snapshot_preview1 as string]: { ...moduleImports },
    } satisfies WebAssembly.Imports,
  }
}

// ── Loader ──────────────────────────────────────────────────────────

const Compile = async (binary: WasmBinary) => {
  const { url, memory64 } = ResolveBinary(binary)
  const response = await UrlLoad(url, "application/wasm")
  const module = await WebAssembly.compileStreaming(response)
  return { module, memory64 }
}

const Instantiate = async (module: WebAssembly.Module, memory64: boolean) => {
  const { imports, exports, conv } = BuildAllImports(memory64)

  const init = () =>
    WebAssembly.instantiate(module, imports).then(instance => {
      RecordAssign(exports, instance.exports)
      const ctors = exports[M.__wasm_call_ctors as string] as
        | (() => void)
        | undefined
      ctors?.()
    })

  await init()

  const dispose = async () => {
    for (const key of Keys(exports)) {
      delete exports[key]
    }
  }

  return { exports, init, dispose, memory64, conv }
}

export const CreateWasmLoader = async (
  binary: WasmBinary
): Promise<() => Promise<RuntimeBridge<number | bigint>>> => {
  const { module, memory64 } = await Compile(binary)

  return async () => {
    const {
      exports,
      init,
      dispose,
      memory64: m64,
      conv,
    } = await Instantiate(module, memory64)

    const addCallback = (
      fn: (...args: (number | bigint)[]) => number | bigint,
      sig: string
    ): WasmAllocated<number | bigint> => {
      const wasmSig = m64
        ? sig[0] + sig.slice(1).replace(/i/g, "j")
        : sig
      const wrapped = ConvertJsFunctionToWasm(
        fn as (...args: number[]) => number | bigint,
        wasmSig
      )
      const table = exports[
        M.__indirect_function_table as string
      ] as WebAssembly.Table
      const index = Number(table.length)
      table.grow((m64 ? 1n : 1) as number)
      table.set((m64 ? BigInt(index) : index) as number, wrapped)
      const pointer = conv(index) as WasmPointer<number | bigint>
      return {
        free: () =>
          (
            exports[M.__indirect_function_table as string] as WebAssembly.Table
          ).set((m64 ? BigInt(index) : index) as number, null),
        pointer,
      }
    }

    const mem = () =>
      (exports[M.memory as string] as WebAssembly.Memory).buffer
    const malloc = () =>
      exports[M.malloc as string] as (size: T) => T

    return {
      memory64: m64,
      conv,
      alloc: (size: number) => Number(malloc()(conv(size))),
      free: (ptr: number, len: number) => {
        ;(exports[M.free as string] as (...args: T[]) => void)(
          conv(ptr),
          conv(len)
        )
      },
      write: (ptr: number, data: Uint8Array) => {
        new Uint8Array(mem()).set(data, ptr)
      },
      read: (ptr: number, len: number) =>
        new Uint8Array(mem(), ptr, len),
      memoryByteLength: () => mem().byteLength,
      call: (name: string, ...args: T[]) =>
        (exports[name] as (...a: T[]) => T)(...args),
      unpackReturn: (ret: number | bigint) => {
        const r = BigInt(ret)
        return [Number(r & 0xFFFFFFFFn), Number(r >> 32n)]
      },
      addCallback,
      init,
      dispose,
    } satisfies RuntimeBridge<T>
  }
}
